import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Simple least-squares linear regression: returns { slope, intercept }
const linearRegression = (points) => {
  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

const pctChange = (curr, prev) => {
  if (prev === 0) return curr === 0 ? 0 : null;
  return ((curr - prev) / prev) * 100;
};

const fmtPct = (pct) => `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`;
const fmtNum = (n) => Math.round(n).toLocaleString();
const fmtMoney = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function DashboardInsights() {
  const [loading, setLoading] = useState(true);
  const [merged, setMerged] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [visitsRes, incomeRes] = await Promise.all([
          axiosInstance.get("dashboard/appointments_graph"),
          axiosInstance.get("dashboard/payments_graph"),
        ]);

        const visitsMap = {};
        (visitsRes.data?.data || []).forEach((item) => {
          visitsMap[item.month] = Number(item.total) || 0;
        });
        const incomeMap = {};
        (incomeRes.data?.data || []).forEach((item) => {
          incomeMap[item.month] = Number(item.total) || 0;
        });

        const currentMonthIdx = new Date().getMonth(); // 0 = January
        const rows = [];
        for (let i = 0; i <= currentMonthIdx; i++) {
          const name = MONTH_NAMES[i];
          rows.push({
            idx: i,
            name,
            visits: visitsMap[name] || 0,
            income: incomeMap[name] || 0,
          });
        }
        setMerged(rows);
      } catch (e) {
        console.error("Failed to load dashboard insights:", e);
        setMerged([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="p-3 text-muted small">Analyzing trends...</div>;
  }

  if (merged.length < 2) {
    return (
      <div className="p-3 text-muted small">
        <i className="ti ti-info-circle me-1" />
        Not enough data yet this year to generate insights.
      </div>
    );
  }

  const insights = [];
  const latest = merged[merged.length - 1];
  const prev = merged[merged.length - 2];

  // ── 1. Trend & % change ──────────────────────────────────────────────
  const visitsChange = pctChange(latest.visits, prev.visits);
  const incomeChange = pctChange(latest.income, prev.income);
  const prevPrev = merged.length >= 3 ? merged[merged.length - 3] : null;
  const prevVisitsChange = prevPrev ? pctChange(prev.visits, prevPrev.visits) : null;
  const prevIncomeChange = prevPrev ? pctChange(prev.income, prevPrev.income) : null;
  if (visitsChange !== null || incomeChange !== null) {
    const lines = [];
    if (visitsChange !== null) {
      lines.push(
        <span key="v">
          Visits are <b className={visitsChange >= 0 ? "text-success" : "text-danger"}>{fmtPct(visitsChange)}</b> vs last month ({fmtNum(prev.visits)} → {fmtNum(latest.visits)}).
        </span>
      );
    }
    if (incomeChange !== null) {
      lines.push(
        <span key="i">
          Income is <b className={incomeChange >= 0 ? "text-success" : "text-danger"}>{fmtPct(incomeChange)}</b> vs last month ({fmtMoney(prev.income)} → {fmtMoney(latest.income)} BHD).
        </span>
      );
    }
    if (prevPrev && (prevVisitsChange !== null || prevIncomeChange !== null)) {
      if (prevVisitsChange !== null) {
        lines.push(
          <span key="pv" className="text-muted">
            {prev.name} visits were <b className={prevVisitsChange >= 0 ? "text-success" : "text-danger"}>{fmtPct(prevVisitsChange)}</b> vs {prevPrev.name} ({fmtNum(prevPrev.visits)} → {fmtNum(prev.visits)}).
          </span>
        );
      }
      if (prevIncomeChange !== null) {
        lines.push(
          <span key="pi" className="text-muted">
            {prev.name} income was <b className={prevIncomeChange >= 0 ? "text-success" : "text-danger"}>{fmtPct(prevIncomeChange)}</b> vs {prevPrev.name} ({fmtMoney(prevPrev.income)} → {fmtMoney(prev.income)} BHD).
          </span>
        );
      }
    }
    insights.push({
      key: "trend",
      icon: "ti-trending-up",
      color: "primary",
      title: "Trend vs Last Month",
      lines,
    });
  }

  // ── 2. Next-month forecast ───────────────────────────────────────────
  if (merged.length >= 3) {
    const visitsReg = linearRegression(merged.map((r) => ({ x: r.idx, y: r.visits })));
    const incomeReg = linearRegression(merged.map((r) => ({ x: r.idx, y: r.income })));
    const nextIdx = latest.idx + 1;
    const forecastVisits = Math.max(0, visitsReg.slope * nextIdx + visitsReg.intercept);
    const forecastIncome = Math.max(0, incomeReg.slope * nextIdx + incomeReg.intercept);
    insights.push({
      key: "forecast",
      icon: "ti-target-arrow",
      color: "info",
      title: "Next-Month Forecast",
      lines: [
        <span key="f">
          Based on the recent trend, expect around <b>{fmtNum(forecastVisits)}</b> visits and <b>{fmtMoney(forecastIncome)} BHD</b> in income next month.
        </span>,
      ],
    });
  }

  // ── 3. Anomaly / spike callouts ──────────────────────────────────────
  if (merged.length >= 3) {
    const findAnomaly = (key) => {
      const values = merged.map((r) => r[key]);
      const mean = values.reduce((s, v) => s + v, 0) / values.length;
      const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
      const std = Math.sqrt(variance);
      if (std === 0 || mean === 0) return null;
      let best = null;
      merged.forEach((r) => {
        const z = Math.abs(r[key] - mean) / std;
        if (z > 1.5 && (!best || z > best.z)) {
          best = { z, row: r, mean, direction: r[key] > mean ? "above" : "below" };
        }
      });
      return best;
    };

    const anomalyLines = [];
    const visitAnomaly = findAnomaly("visits");
    if (visitAnomaly) {
      const pctOff = Math.abs(((visitAnomaly.row.visits - visitAnomaly.mean) / visitAnomaly.mean) * 100);
      anomalyLines.push(
        <span key="av">
          <b>{visitAnomaly.row.name}</b> visits were unusually {visitAnomaly.direction} average ({fmtNum(visitAnomaly.row.visits)}, ~{pctOff.toFixed(0)}% {visitAnomaly.direction} average).
        </span>
      );
    }
    const incomeAnomaly = findAnomaly("income");
    if (incomeAnomaly) {
      const pctOff = Math.abs(((incomeAnomaly.row.income - incomeAnomaly.mean) / incomeAnomaly.mean) * 100);
      anomalyLines.push(
        <span key="ai">
          <b>{incomeAnomaly.row.name}</b> income was unusually {incomeAnomaly.direction} average (~{pctOff.toFixed(0)}% {incomeAnomaly.direction} average).
        </span>
      );
    }
    if (anomalyLines.length > 0) {
      insights.push({
        key: "anomaly",
        icon: "ti-alert-triangle",
        color: "warning",
        title: "Anomaly Detected",
        lines: anomalyLines,
      });
    }
  }

  // ── 4. Revenue-per-visit efficiency ──────────────────────────────────
  const withVisits = merged.filter((r) => r.visits > 0);
  if (withVisits.length >= 2) {
    const curr = withVisits[withVisits.length - 1];
    const prior = withVisits[withVisits.length - 2];
    const currRatio = curr.income / curr.visits;
    const priorRatio = prior.income / prior.visits;
    const ratioChange = pctChange(currRatio, priorRatio);
    if (ratioChange !== null) {
      insights.push({
        key: "efficiency",
        icon: "ti-coin",
        color: "success",
        title: "Revenue per Visit",
        lines: [
          <span key="e">
            Average revenue per visit {ratioChange >= 0 ? "rose" : "fell"} from <b>{currRatio && priorRatio ? fmtMoney(priorRatio) : "-"}</b> to <b>{fmtMoney(currRatio)} BHD</b> ({fmtPct(ratioChange)}).
          </span>,
        ],
      });
    }
  }

  if (insights.length === 0) {
    return (
      <div className="p-3 text-muted small">
        <i className="ti ti-info-circle me-1" />
        Not enough data yet this year to generate insights.
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="aiInsightsGrid">
        {insights.map((insight) => (
          <div key={insight.key} className={`aiInsightCard aiInsightCard-${insight.color}`}>
            <div className="aiInsightIcon">
              <i className={`ti ${insight.icon}`} />
            </div>
            <div className="aiInsightBody">
              <div className="aiInsightTitle">{insight.title}</div>
              {insight.lines.map((line, i) => (
                <div className="aiInsightLine" key={i}>{line}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
